// BrandSinglePage.js - Clean rebuild with proper layout
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Star, ExternalLink, Gift, ChevronLeft, Edit, ChevronDown, ChevronUp, MessageSquare, Send, User, Reply } from 'lucide-react';
import BrandPhotoMasonry from '../components/brand/BrandPhotoMasonry';
import { useAuth } from '../context/AuthContext';
import commentService from '../services/CommentService';

function BrandSinglePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [brand, setBrand] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Comment states
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Reply states
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAuthorName, setReplyAuthorName] = useState('');
  const [replyAuthorEmail, setReplyAuthorEmail] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Auto-hide notifications after 2 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Pre-fill user name if logged in
  useEffect(() => {
    if (user && user.displayName) {
      setAuthorName(user.displayName);
      setReplyAuthorName(user.displayName);
    }
  }, [user]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  useEffect(() => {
    const fetchBrandBySlug = async () => {
      try {
        setLoading(true);

        // Query the brands collection by slug
        const brandsQuery = query(
          collection(db, "brands"),
          where("slug", "==", slug)
        );

        const querySnapshot = await getDocs(brandsQuery);

        if (querySnapshot.empty) {
          setError("Brand not found");
          setLoading(false);
          return;
        }

        // Get the first document that matches the slug
        const brandDoc = querySnapshot.docs[0];
        const brandData = { id: brandDoc.id, ...brandDoc.data() };
        setBrand(brandData);

        // Fetch promotions from subcollection
        const promotionsRef = collection(doc(db, "brands", brandDoc.id), "promotions");
        const promotionsSnapshot = await getDocs(promotionsRef);

        if (!promotionsSnapshot.empty) {
          const promotionsData = [];
          promotionsSnapshot.forEach(doc => {
            promotionsData.push({ id: doc.id, ...doc.data() });
          });

          // Sort promotions by createdAt date if available
          promotionsData.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return b.createdAt.toDate() - a.createdAt.toDate();
            }
            return 0;
          });

          setPromotions(promotionsData);
        }

        // Fetch comments for this brand
        await fetchBrandComments(brandDoc.id);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching brand:", err);
        setError("Failed to load brand details");
        setLoading(false);
      }
    };

    fetchBrandBySlug();
  }, [slug]);

  // Fetch comments for the brand
  const fetchBrandComments = async (brandId) => {
    try {
      setCommentsLoading(true);
      console.log('Fetching comments for brand:', brandId);
      const result = await commentService.getBrandComments(brandId);

      console.log('Comments fetch result:', result);
      if (result.success) {
        setComments(result.data);
        console.log('Comments loaded:', result.data.length);
      } else {
        console.error('Error fetching comments:', result.error);
        showNotification('Failed to load comments: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      showNotification('Failed to load comments', 'error');
    } finally {
      setCommentsLoading(false);
    }
  };

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      showNotification('Please enter a comment', 'error');
      return;
    }

    if (!authorName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    // Check character count (limit to 1000 characters)
    if (commentText.length > 1000) {
      showNotification(`Comment is too long. Please limit to 1000 characters (currently ${commentText.length} characters)`, 'error');
      return;
    }

    try {
      setSubmittingComment(true);

      const result = await commentService.addComment(
        brand.id,
        brand.name,
        commentText,
        authorName,
        authorEmail
      );

      if (result.success) {
        showNotification('Comment added successfully!', 'success');

        // Create new comment object to add immediately to the list
        const newComment = {
          id: result.commentId,
          text: commentText,
          authorName: authorName,
          authorEmail: authorEmail,
          relatedContent: brand.name,
          relatedContentId: brand.id,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'active',
          replies: []
        };

        // Add comment to the beginning of the list (newest first)
        setComments(prevComments => [newComment, ...prevComments]);

        // Clear form
        setCommentText('');
        if (!user) {
          setAuthorName('');
        }
        setAuthorEmail('');
      } else {
        showNotification('Failed to add comment: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      showNotification('Failed to add comment', 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle reply button click
  const handleReplyClick = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
    if (!user) {
      setReplyAuthorName('');
      setReplyAuthorEmail('');
    }
  };

  // Handle reply submission
  const handleReplySubmit = async (commentId) => {
    if (!replyText.trim()) {
      showNotification('Please enter a reply message', 'error');
      return;
    }

    if (!replyAuthorName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    // Check character count for replies (limit to 500 characters)
    if (replyText.length > 500) {
      showNotification(`Reply is too long. Please limit to 500 characters (currently ${replyText.length} characters)`, 'error');
      return;
    }

    try {
      setSubmittingReply(true);

      const result = await commentService.addUserReply(
        commentId,
        replyText,
        replyAuthorName,
        replyAuthorEmail
      );

      if (result.success) {
        showNotification('Reply added successfully!', 'success');

        // Create new reply object to add immediately to the comment
        const newReply = {
          id: result.replyId,
          commentId: commentId,
          text: replyText,
          userName: replyAuthorName,
          userEmail: replyAuthorEmail,
          replyType: 'user',
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // Add reply to the specific comment
        setComments(prevComments =>
          prevComments.map(comment =>
            comment.id === commentId
              ? { ...comment, replies: [...(comment.replies || []), newReply] }
              : comment
          )
        );

        // Clear reply form
        setReplyingTo(null);
        setReplyText('');
        if (!user) {
          setReplyAuthorName('');
          setReplyAuthorEmail('');
        }
      } else {
        showNotification('Failed to add reply: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      showNotification('Failed to add reply', 'error');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Handle cancel reply
  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
    if (!user) {
      setReplyAuthorName('');
      setReplyAuthorEmail('');
    }
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

  // Get unique categories from promotions with custom sorting
  const categories = React.useMemo(() => {
    if (!promotions.length) return ['All'];
    const uniqueCategories = [...new Set(promotions.map(p => p.category).filter(Boolean))];

    // Custom sorting order
    const sortOrder = [
      'Welcome Bonus',
      'Rebate (by turnover)',
      'Reload (daily/weekly)',
      'Referral',
      'Others'
    ];

    const sorted = uniqueCategories.sort((a, b) => {
      const indexA = sortOrder.indexOf(a);
      const indexB = sortOrder.indexOf(b);

      // If both are in sortOrder, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only A is in sortOrder (but not at the end positions), it comes first
      if (indexA !== -1 && indexA < sortOrder.length - 2) {
        return -1;
      }

      // If only B is in sortOrder (but not at the end positions), it comes first
      if (indexB !== -1 && indexB < sortOrder.length - 2) {
        return 1;
      }

      // If A is 'Referral', it goes near the end
      if (a === 'Referral') {
        return b === 'Others' ? -1 : 1;
      }

      // If B is 'Referral', it goes near the end
      if (b === 'Referral') {
        return a === 'Others' ? 1 : -1;
      }

      // If A is 'Others', it goes to the end
      if (a === 'Others') {
        return 1;
      }

      // If B is 'Others', it goes to the end
      if (b === 'Others') {
        return -1;
      }

      // For middle categories, sort alphabetically
      return a.localeCompare(b);
    });

    return ['All', ...sorted];
  }, [promotions]);

  // Filter promotions by selected category
  const filteredPromotions = React.useMemo(() => {
    if (selectedCategory === 'All') return promotions;
    return promotions.filter(p => p.category === selectedCategory);
  }, [promotions, selectedCategory]);

  // Star Rating Component
  const StarRating = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center justify-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={20}
            className={`${i < fullStars
              ? "text-yellow-400 fill-yellow-400"
              : i === fullStars && hasHalfStar
                ? "text-yellow-400 fill-yellow-400"
                : "text-gray-300"
              }`}
          />
        ))}
        <span className="ml-2 text-sm font-semibold">{rating}</span>
      </div>
    );
  };

  // Show loading only for initial data fetch, not for images
  if (loading) {
    return (
      <div className="pb-8 p-4 max-w-4xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading brand details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4 text-center py-12">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <button
          onClick={handleBackClick}
          className="text-blue-600 hover:underline flex items-center justify-center"
        >
          <ChevronLeft size={16} className="mr-1" /> Back
        </button>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="max-w-4xl mx-auto p-4 text-center py-12">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
          <p className="text-red-700">Brand not found</p>
        </div>
        <button
          onClick={handleBackClick}
          className="text-blue-600 hover:underline flex items-center justify-center"
        >
          <ChevronLeft size={16} className="mr-1" /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="pb-8 p-4 w-full mx-auto">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${notification.type === 'success'
          ? 'bg-green-100 border border-green-400 text-green-700'
          : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Navigation and Website Button Row */}
      <div className="flex justify-between items-center mb-4 max-w-7xl mx-auto">
        <button
          onClick={handleBackClick}
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back
        </button>

        {/* Action Buttons - Aligned right */}
        <div className="flex space-x-3">
          {/* Edit Button for Admin */}
          {userRole === 'admin' && (
            <button
              onClick={() => navigate(`/admin/brand/edit/${brand.id}`)}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors"
            >
              <Edit size={16} className="mr-2" />
              Edit
            </button>
          )}

          {/* Visit Website Button */}
          <a
            href={brand.websiteUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors
              ${brand.websiteUrl
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-300 cursor-not-allowed text-gray-600"}`}
            onClick={(e) => {
              if (!brand.websiteUrl) {
                e.preventDefault();
                alert("Website URL not available");
              }
            }}
          >
            <ExternalLink size={16} className="mr-2" />
            Visit Website
          </a>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Brand Header */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Cover Image with lazy loading */}
              <div className="relative h-64 md:h-80 bg-gray-200">
                {brand.coverImage ? (
                  <img
                    src={brand.coverImage}
                    alt={`${brand.name} cover`}
                    className="w-full h-full object-cover object-left-top"
                    loading="lazy"
                    onLoad={(e) => {
                      e.target.style.opacity = '1';
                    }}
                    style={{ opacity: '0', transition: 'opacity 0.3s ease' }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-blue-100">
                    <p className="text-blue-500">No cover image available</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>

              {/* Centered Brand Info - Fixed logo position */}
              <div className="p-6 text-center relative">
                {/* Centered Brand Logo - positioned to clearly separate from cover */}
                <div className="flex justify-center relative -mt-20 mb-6">
                  <div className="w-24 h-24 bg-white rounded-full shadow-lg p-1 border-1 border-white">
                    {brand.logoImage ? (
                      <img
                        src={brand.logoImage}
                        alt={`${brand.name} logo`}
                        className="w-full h-full object-contain rounded-full"
                        loading="lazy"
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        style={{ opacity: '0', transition: 'opacity 0.3s ease' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center rounded-full bg-blue-100">
                        <p className="text-blue-500 font-bold text-xl">{brand.name.charAt(0)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Centered Brand Title and Stars */}
                <h1 className="text-2xl font-bold text-gray-800 mb-2">{brand.name}</h1>
                <StarRating rating={brand.rating} />

                {/* Description - Now center aligned with show/hide functionality */}
                <div className="mt-4 text-center px-4">
                  <div
                    className={`text-gray-700 ${!showFullDescription ? 'overflow-hidden' : ''}`}
                    style={!showFullDescription ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    } : {}}
                  >
                    <p>{brand.description}</p>
                  </div>
                  {brand.description && brand.description.length > 200 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="mt-2 inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {showFullDescription ? 'Show less' : 'Show all'}
                      {showFullDescription ? (
                        <ChevronUp size={16} className="ml-1" />
                      ) : (
                        <ChevronDown size={16} className="ml-1" />
                      )}
                    </button>
                  )}
                </div>

                {/* Welcome Bonus - Direct content without label */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <div className="flex items-center justify-center">
                    <Gift className="text-blue-600 w-5 h-5 mr-2" />
                    <p className="font-bold text-blue-600 text-lg">Welcome Bonus: {brand.welcomeBonus}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Promotions Section with BrandPhotoMasonry */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-blue-600 p-4 text-white">
                <h2 className="text-xl font-semibold">Promotions</h2>
              </div>

              <div className="p-6">
                {promotions && promotions.length > 0 ? (
                  <div>
                    {/* Category Filter Slider */}
                    {categories.length > 1 && (
                      <div className="mb-6">
                        <div className="overflow-x-auto pb-2">
                          <div className="flex space-x-2 min-w-max">
                            {categories.map(category => (
                              <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                              >
                                {category}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {filteredPromotions.length > 0 ? (
                      <BrandPhotoMasonry
                        promotions={filteredPromotions}
                        brandName={brand.name}
                      />
                    ) : (
                      <div className="text-center p-6 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">No promotions available in {selectedCategory} category.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No promotions available for this brand.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Comments Section */}
          <div className="lg:col-span-1">
            <div className="">
              <div className=" px-4 py-2">
                <h2 className="text-lg font-semibold flex items-center">
                  <MessageSquare className="mr-2" size={18} />
                  Comments ({comments.length})
                </h2>
              </div>

              <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Comment Form */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Leave a Comment</h3>
                  <form onSubmit={handleCommentSubmit} className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          id="authorName"
                          value={authorName}
                          onChange={(e) => setAuthorName(e.target.value)}
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Your name"
                          required
                          maxLength="100"
                          disabled={user && user.displayName}
                        />
                      </div>
                      {!user && (
                        <div>
                          <label htmlFor="authorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                            Email (optional)
                          </label>
                          <input
                            type="email"
                            id="authorEmail"
                            value={authorEmail}
                            onChange={(e) => setAuthorEmail(e.target.value)}
                            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="your@email.com"
                            maxLength="100"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="commentText" className="block text-sm font-medium text-gray-700 mb-2">
                        Comment *
                      </label>
                      <textarea
                        id="commentText"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors"
                        rows="4"
                        placeholder="Share your thoughts about this brand..."
                        required
                        maxLength="1000"
                      />
                      <div className="text-right text-sm text-gray-500 mt-2">
                        {commentText ? commentText.length : 0}/1000 characters
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={submittingComment || !commentText.trim() || !authorName.trim()}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white text-base font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {submittingComment ? 'Posting...' : 'Post Comment'}
                    </button>
                  </form>
                </div>

                {/* Comments List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      All Comments
                    </h3>
                    <button
                      onClick={() => {
                        console.log('Manual refresh clicked');
                        commentService.debugCheckComments();
                        fetchBrandComments(brand.id);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Refresh
                    </button>
                  </div>

                  {commentsLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-base text-gray-600">Loading comments...</p>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-base text-gray-500">No comments yet!</p>
                      <p className="text-sm text-gray-400 mt-2">Be the first to share your thoughts about this brand.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                          {/* Comment Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                                <User className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="text-base font-semibold text-gray-900">{comment.authorName}</div>
                                <div className="text-sm text-gray-500">
                                  {commentService.formatDate(comment.createdAt)}
                                </div>
                              </div>
                            </div>

                            {/* Reply Button */}
                            <button
                              onClick={() => handleReplyClick(comment.id)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Reply className="w-4 h-4 mr-2" />
                              Reply
                            </button>
                          </div>

                          {/* Comment Text */}
                          <div className="mb-4">
                            <p className="text-base text-gray-700 leading-relaxed whitespace-pre-wrap">{comment.text}</p>
                          </div>

                          {/* Reply Form */}
                          {replyingTo === comment.id && (
                            <div className="mb-4 bg-blue-50 rounded-xl p-4 border border-blue-100">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">Reply to {comment.authorName}</h4>
                              <div className="space-y-3">
                                <div className="space-y-3">
                                  <div>
                                    <input
                                      type="text"
                                      value={replyAuthorName}
                                      onChange={(e) => setReplyAuthorName(e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      placeholder="Your name"
                                      required
                                      maxLength="100"
                                      disabled={user && user.displayName}
                                    />
                                  </div>
                                  {!user && (
                                    <div>
                                      <input
                                        type="email"
                                        value={replyAuthorEmail}
                                        onChange={(e) => setReplyAuthorEmail(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        placeholder="your@email.com (optional)"
                                        maxLength="100"
                                      />
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors"
                                    rows="3"
                                    placeholder="Type your reply..."
                                    required
                                    maxLength="500"
                                  />
                                  <div className="text-right text-sm text-gray-500 mt-2">
                                    {replyText ? replyText.length : 0}/500 characters
                                  </div>
                                </div>

                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={handleCancelReply}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleReplySubmit(comment.id)}
                                    disabled={submittingReply || !replyText.trim() || !replyAuthorName.trim()}
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Send className="w-4 h-4 mr-2" />
                                    {submittingReply ? 'Sending...' : 'Send Reply'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* All Replies (Admin and User) */}
                          {comment.replies && comment.replies.length > 0 && (
                            <div className="border-l-4 border-gray-200 pl-4 ml-6 space-y-4 mt-4">
                              <div className="text-sm font-semibold text-gray-700 flex items-center">
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Replies ({comment.replies.length})
                              </div>
                              {comment.replies.map((reply) => (
                                <div key={reply.id} className={`rounded-lg p-4 ${reply.replyType === 'admin' || reply.adminName
                                  ? 'bg-green-50 border border-green-200'
                                  : 'bg-blue-50 border border-blue-200'
                                  }`}>
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${reply.replyType === 'admin' || reply.adminName
                                      ? 'bg-gradient-to-br from-green-500 to-green-600'
                                      : 'bg-gradient-to-br from-blue-500 to-blue-600'
                                      }`}>
                                      <span className="text-sm text-white font-bold">
                                        {reply.replyType === 'admin' || reply.adminName ? 'A' : 'U'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className={`text-sm font-semibold ${reply.replyType === 'admin' || reply.adminName
                                        ? 'text-green-700'
                                        : 'text-blue-700'
                                        }`}>
                                        {reply.adminName || reply.userName}
                                        {reply.replyType === 'admin' || reply.adminName ? ' (Admin)' : ''}
                                      </span>
                                      <div className="text-xs text-gray-500">
                                        {commentService.formatDate(reply.createdAt)}
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed">{reply.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandSinglePage;